import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { catLabel } from "@/components/common";
import { toast } from "sonner";
import { CheckCircle2, Circle, Plus, ArrowLeft, ArrowRight, Sparkles, X } from "lucide-react";

const STEPS = [
  { type: "product" }, { type: "material" }, { type: "glass" }, { type: "accessory" }, { type: "service" },
];

export default function OnboardingWizard({ onClose, onDone, initialStep = 0 }) {
  const { t } = useTranslation();
  const resume = initialStep > 0 && initialStep < STEPS.length;
  const [phase, setPhase] = useState(resume ? "step" : "intro"); // intro | step | done
  const [idx, setIdx] = useState(resume ? initialStep : 0);
  const [demo, setDemo] = useState(null);

  const runDemo = () => {
    const s = "Mi servono 4 finestre PVC bianche con doppio vetro";
    const qty = (s.match(/\b(\d+)\b/) || [])[1] || "4";
    const prod = /finestr/i.test(s) ? "Finestre" : "—";
    const mat = /pvc/i.test(s) ? "PVC" : /alluminio/i.test(s) ? "Alluminio" : "—";
    const col = /bianc/i.test(s) ? "Bianco" : "—";
    const glass = /doppio vetro/i.test(s) ? "Doppio vetro" : "—";
    setDemo({ s, items: [["Quantità", qty], ["Prodotto", prod], ["Materiale", mat], ["Colore", col], ["Vetro", glass]] });
  };
  const [items, setItems] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [counts, setCounts] = useState({});
  const step = STEPS[idx];

  const load = useCallback((type) => {
    http.get(`/catalog?type=${type}`).then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { if (phase === "step") load(step.type); }, [phase, idx, step, load]);

  const toggle = async (it, active) => {
    setItems((p) => p.map((x) => (x.id === it.id ? { ...x, active } : x)));
    try {
      if (it.is_custom) await http.put(`/catalog/custom/${it.id}`, { active });
      else await http.post("/catalog/toggle", { type: step.type, master_key: it.master_key, active });
    } catch (e) { toast.error(apiErr(e)); load(step.type); }
  };
  const addCustom = async () => {
    if (!newLabel.trim()) return;
    try { await http.post("/catalog/custom", { type: step.type, label: newLabel.trim() }); setNewLabel(""); load(step.type); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const saveStep = async (goNext) => {
    setCounts((c) => ({ ...c, [step.type]: items.filter((i) => i.active).length }));
    await http.post("/onboarding/wizard", { step: idx + 1 }).catch(() => {});
    if (goNext && idx < STEPS.length - 1) setIdx(idx + 1);
    else if (goNext) { await http.post("/onboarding/wizard", { completed: true }).catch(() => {}); setPhase("done"); }
  };

  const skipAll = async () => { await http.post("/onboarding/wizard", { step: idx, skipped: true }).catch(() => {}); onClose?.(); };
  const start = async () => { await http.post("/onboarding/wizard", { step: 0 }).catch(() => {}); setPhase("step"); };

  return (
    <div className="fixed inset-0 z-50 bg-[#0B1120]/95 backdrop-blur flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 font-heading font-bold text-[#0F172A]"><Sparkles className="w-4 h-4 text-sky-500" /> NEXTMIND INFISSI</div>
          <button data-testid="wizard-close" onClick={() => (onClose?.())} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {phase === "intro" && (
          <div className="p-8 text-center space-y-4">
            <h2 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("wizard.intro_title")}</h2>
            <p className="text-slate-500">{t("wizard.intro_sub")}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button data-testid="wizard-start" onClick={start} className="bg-[#0F172A]">{t("wizard.start_btn")}</Button>
              <Button data-testid="wizard-skip" variant="outline" onClick={skipAll}>{t("wizard.skip_btn")}</Button>
            </div>
          </div>
        )}

        {phase === "step" && (
          <div className="p-5 sm:p-6 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-sky-600" data-testid="wizard-progress">{idx + 1} / {STEPS.length}</span>
              <div className="flex-1 mx-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${((idx + 1) / STEPS.length) * 100}%` }} /></div>
              <button data-testid="wizard-skip-later" onClick={skipAll} className="text-xs font-medium text-slate-400 hover:text-slate-600 whitespace-nowrap">{t("wizard.skip")}</button>
            </div>
            <h3 className="font-heading text-xl font-bold text-[#0F172A]">{t(`wizard.st_${step.type}`)}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="wizard-items">
              {items.map((it) => (
                <button key={it.id} data-testid={`wizard-item-${it.key}`} onClick={() => toggle(it, !it.active)}
                  className={`flex items-center gap-2 p-3 rounded-xl border text-left text-sm transition-colors ${it.active ? "border-sky-500 bg-sky-50 text-[#0F172A]" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  {it.active ? <CheckCircle2 className="w-4 h-4 text-sky-500 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
                  <span className="truncate">{catLabel(t, it)}</span>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="wizard-custom-input" placeholder={t("wizard.custom_ph")} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom()} />
              <Button data-testid="wizard-custom-add" variant="outline" onClick={addCustom}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" disabled={idx === 0} onClick={() => setIdx(idx - 1)}><ArrowLeft className="w-4 h-4 mr-1" /> {t("common.back")}</Button>
              <div className="flex gap-2">
                <Button data-testid="wizard-step-skip" variant="ghost" className="text-slate-500" onClick={() => saveStep(true)}>{t("wizard.skip")}</Button>
                <Button data-testid="wizard-step-next" onClick={() => saveStep(true)} className="bg-[#0F172A]">
                  {idx === STEPS.length - 1 ? t("wizard.complete") : t("wizard.save_continue")} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {phase === "done" && (
          <div className="p-8 text-center space-y-4">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h2 className="font-heading text-2xl font-extrabold text-[#0F172A]">{t("wizard.done_title2")}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
              {STEPS.map((s) => (
                <div key={s.type} className="bg-slate-50 rounded-lg p-2"><div className="text-lg font-bold text-[#0F172A]">{counts[s.type] ?? "—"}</div><div className="text-[10px] text-slate-500">{t(`catalog.${s.type}`)}</div></div>
              ))}
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 text-left">
              <button data-testid="wizard-try-assistant" onClick={runDemo} className="text-sm font-semibold text-sky-700">✨ {t("wizard.try_ai")}</button>
              {demo && (
                <div className="mt-3" data-testid="wizard-demo-result">
                  <div className="text-xs text-slate-500 italic">"{demo.s}"</div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
                    {demo.items.map(([k, v]) => (
                      <div key={k} className="bg-white rounded-lg p-2 border border-sky-100"><div className="text-[10px] text-slate-400">{k}</div><div className="text-sm font-bold text-[#0F172A]">{v}</div></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="text-xs text-slate-400">{t("wizard.done_hint")}</p>
            <Button data-testid="wizard-goto-dashboard" onClick={() => { onDone?.(); onClose?.(); }} className="bg-[#0F172A]">{t("wizard.goto_dashboard")}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
